/**
 * Geolocation Service
 *
 * Handles browser geolocation API with Promise wrapper.
 * Provides clean abstraction for getting user's current location.
 *
 * @remarks
 * Separates geolocation concerns from component logic for better
 * testability and reusability across the application.
 */

export interface GeolocationResult {
  lat: number;
  lng: number;
}

export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

export class GeolocationError extends Error {
  constructor(
    message: string,
    public code?: number,
  ) {
    super(message);
    this.name = "GeolocationError";
  }
}

/**
 * Geolocation Service
 *
 * @remarks
 * Promise-based wrapper around browser Geolocation API.
 * Handles browser support detection and error cases.
 */
export class GeolocationService {
  /**
   * Check if geolocation is supported by the browser
   */
  static isSupported(): boolean {
    return "geolocation" in navigator;
  }

  /**
   * Get user's current location
   *
   * @param options - Geolocation API options
   * @returns Promise resolving to lat/lng coordinates
   * @throws GeolocationError if not supported or permission denied
   *
   * @remarks
   * Default options prioritize accuracy and fresh data:
   * - enableHighAccuracy: true (use GPS if available)
   * - timeout: 10 seconds
   * - maximumAge: 0 (no cached position)
   */
  static async getCurrentLocation(
    options: GeolocationOptions = {},
  ): Promise<GeolocationResult> {
    if (!this.isSupported()) {
      throw new GeolocationError(
        "Geolocation is not supported by this browser",
      );
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    };

    const mergedOptions = { ...defaultOptions, ...options };

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            mergedOptions,
          );
        },
      );

      return {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
    } catch (error) {
      // Wrap native GeolocationPositionError for consistent error handling
      const geoError = error as GeolocationPositionError;
      throw new GeolocationError(geoError.message, geoError.code);
    }
  }
}
